<script>
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { BRANCH_TABS } from '../constants/branch.js';
import GeneralTab from './Tabs/GeneralTab.vue';
import StaffScheduleTab from './Tabs/StaffScheduleTab.vue';
import ServiceTab from './Tabs/ServiceTab.vue';
import StaffTab from './Tabs/StaffTab.vue';
import RoomTab from './Tabs/RoomTab.vue';

export default {
  components: {
    BackButton,
    SettingIntroBanner,
    LoadingState,
    GeneralTab,
    StaffScheduleTab,
    ServiceTab,
    StaffTab,
    RoomTab,
  },
  data() {
    return {
      isLoading: false,
      selectedTabIndex: 0,
    }
  },
  watch: {
    '$route.query.tab': {
      handler(newTab) {
        const tabIndex = parseInt(newTab, 10);
        if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < this.tabs.length) {
          this.selectedTabIndex = tabIndex;
        }
      },
      immediate: true,
    },
  },
  computed: {
    showRoomsTab() {
      return this.$store.getters['aoikumo/isConnected'];
    },
    tabs() {
      return [
        ...BRANCH_TABS,
        ...(this.showRoomsTab ? [{ key: 'rooms', name: 'Rooms' }] : []),
      ];
    },
    branchId() {
      return this.$route.params.branchId;
    },
    selectedBranch() {
      return this.$store.state.branch.selectedBranch;
    },
    branchName() {
      return this.selectedBranch?.branch_name || 'Branch Management';
    },
    isOnboardingAvailability() {
      const step = this.$route.query.step;
      return this.$route.query.onboarding === 'true' && ['setup_staff_schedule', 'link_staff_to_schedule'].includes(step);
    },
    isOnboardingService() {
      const step = this.$route.query.step;
      return this.$route.query.onboarding === 'true' && ['link_service_to_branch', 'link_staff_to_service'].includes(step);
    },
    isOnboardingStaff() {
      return this.$route.query.onboarding === 'true' && this.$route.query.step === 'link_staff_to_branch';
    },
    isOnboardingBranch() {
      return this.$route.query.onboarding === 'true';
    },
    onboardingIndex() {
      if (this.isOnboardingAvailability) {
        return 1;
      } else if (this.isOnboardingService) {
        return 2;
      } else if (this.isOnboardingStaff) {
        return 3;
      } 
    },
    pageTitle() {
      if (this.selectedTabIndex === 0) {
        return `${this.branchName}/General`
      } else if (this.selectedTabIndex === 1) {
        return `${this.branchName}/Availability`
      } else if (this.selectedTabIndex === 2) {
        return `${this.branchName}/Service`
      } else if (this.selectedTabIndex === 3) {
        return `${this.branchName}/Staff`
      } else if (this.selectedTabIndex === 4) {
        return `${this.branchName}/Rooms`
      } else {
        return `${this.branchName}`
      }
    }
  },
  methods: {
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
      this.$router.push({
        name: 'organization.branches.details',
        params: this.$route.params,
        query: { ...this.$route.query, tab: selectedTabIndex },
      });
    },
  },
  async mounted(){
    this.isLoading = true;
    try {
      await this.$store.dispatch('branch/fetchBranch', this.branchId);
    } catch (error) {
      console.error('Error fetching branch:', error);
    } finally {
      this.isLoading = false;
    }
  }
}
</script>

<template>
<div class="flex-1 overflow-auto">
  <div class="flex justify-between items-center h-14 min-h-[3.5rem] px-4 py-2 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800/50">
    <h1 class="flex items-center mb-0 text-2xl text-slate-900 dark:text-slate-100">
      <BackButton class="ml-2 mr-4" :backUrl="{ name: 'organization.branches' }"/>
      <fluent-icon
        icon="building-bank"
        class="hidden ml-1 mr-2 rtl:ml-2 rtl:mr-1 md:block"
      />
      <span class="text-2xl font-medium text-slate-900 dark:text-slate-100">
        {{ pageTitle }}
      </span>
    </h1>
  </div>
  <SettingIntroBanner
    header-title="Branch Management"
    class="mb-8"
  >
    <div class="relative inline-block">
      <woot-tabs
        class="settings--tabs"
        :index="selectedTabIndex"
        :border="false"
        @change="onTabChange"
      >
        <woot-tabs-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :name="tab.name"
          :show-badge="false"
        />
      </woot-tabs>
      
      <!-- Onboarding Hint Tooltip -->
      <div v-if="isOnboardingBranch && onboardingIndex !== undefined" class="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div 
          v-for="(tab, index) in tabs" 
          :key="`tooltip-${tab.key}`"
          v-show="index === onboardingIndex && selectedTabIndex !== onboardingIndex"
          class="absolute -bottom-10 transform translate-x-1 whitespace-nowrap z-10"
          :style="{ left: `${(index * 100 / tabs.length) + (50 / tabs.length)}%` }"
        >
          <div class="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-3 py-2 rounded-md shadow-lg animate-bounce-subtle flex gap-1 items-center">
            <fluent-icon icon="caret-up" /> Hint: Navigate to the tab
          </div>
        </div>
      </div>
    </div>
  </SettingIntroBanner>

  <LoadingState v-if="isLoading" message="Loading branch details..." />

  <GeneralTab v-else-if="selectedTabIndex === 0" :selected-branch="selectedBranch" />

  <StaffScheduleTab v-else-if="selectedTabIndex === 1" :branchId="branchId" />
  
  <ServiceTab v-else-if="selectedTabIndex === 2" />

  <StaffTab v-else-if="selectedTabIndex === 3" />

  <RoomTab v-else-if="selectedTabIndex === 4" @go-general="onTabChange(0)" />
</div>
</template>